import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * 화면 사이 링크(2026-10-02 way — 정산현황 줄 → 원천 화면 · 출납일보 ↔ 영업정산 ↔ OPS 탭).
 *
 * 받는 쪽은 URL 의 조건을 **들어올 때 한 번** 화면 상태에 넣는다(그 뒤 사람이 바꾸면 그 값이 이긴다).
 *   `?mon=YYYY-MM` — 정산년월(`useStickyDriveMon` 이 첫 렌더에 읽는다 · 기억에도 쓴다)
 *   `?q=…`        — 검색칸(목록마다 숫자만 치면 NO 로도 찾는다 · `paging` qId)
 *   `?id=…`       — 영업정산 한 줄 고정(`listResultReq.resultReqId`)
 *   `/accounting?tab=cash&cash=<id>&date=YYYY-MM-DD` — 출납일보 그 날·그 줄(굵은 테두리 + 스크롤)
 *   `/accounting?tab=opsTransit&dy=DY…` — OPS 탭 그 DY 만
 *   `/settle?tab=misu&view=cancel` — 미수금 취소요청 보기 · `/settle?tab=cust&recv=<id>&mon=YYYY-MM` — 거래처별 정산 그 줄 상세
 * 🔴 링크 주소는 아래 함수로만 만든다 — 손으로 쓰면 받는 쪽 이름과 언젠가 갈린다.
 */
export const link = {
  resultReq: (id: number | string, mon?: string | null) => `/result-req?id=${id}${mon ? `&mon=${mon}` : ''}`,
  cashBook: (id: number | string, date?: string | null) => `/accounting?tab=cash&cash=${id}${date ? `&date=${date}` : ''}`,
  opsTransit: (dyNo: string) => `/accounting?tab=opsTransit&dy=${encodeURIComponent(dyNo)}`,
  resultDaily: (mon: string, q: string | number) => `/result-daily?mon=${mon}&q=${encodeURIComponent(String(q))}`,
  resultMo: (mon: string, q: string) => `/result-mo?mon=${mon}&q=${encodeURIComponent(q)}`,
  finInst: (q: string) => `/fin-inst?q=${encodeURIComponent(q)}`,
  dispatch: (recvId: number | string) => `/dispatch?q=${recvId}`,
  // 🔴 아래 둘은 서버도 같은 모양으로 만든다(구글챗 DM 버튼 — `functions/src/misuCancel.ts` notifyRequested·notifyDecided).
  //    한쪽만 바꾸면 알림 버튼이 엉뚱한 화면을 연다.
  /** 회계 › 발행 요청 — 그 취소·수정 요청 창(10-19 일원화 · 처리는 여기서). */
  misuCancel: (id: number | string) => `/accounting?tab=invreq&mcr=${id}`,
  /** 미수금 › 취소·수정 결과 보기(조회만). */
  misuCancelResults: () => '/settle?tab=misu&view=cancel',
  /** 거래처별 정산 그 접수·정산월 상세(청구 창에서 취소 요청 결과를 본다). */
  custBill: (recvId: number | string, mon: string) => `/settle?tab=cust&recv=${recvId}&mon=${mon}`,
};

/** 지금 주소의 조건 하나(없으면 null). */
export function urlParam(search: string, name: string): string | null {
  try {
    const v = new URLSearchParams(search).get(name);
    return v === null || v.trim() === '' ? null : v.trim();
  } catch {
    return null;
  }
}

/**
 * `?q=` 로 들어오면 검색칸·검색어를 그 값으로(주소가 바뀔 때마다 한 번). `?mon=` 은 이미 이 화면에 있을 때도 따라가게
 * `setMon` 을 주면 같이 맞춘다(처음 들어올 때는 `useStickyDriveMon` 이 먼저 읽는다).
 */
export function useUrlSearch(setTerm: (v: string) => void, setQ: (v: string) => void, setMon?: (m: string) => void): void {
  const { search } = useLocation();
  useEffect(() => {
    const q = urlParam(search, 'q');
    if (q !== null) { setTerm(q); setQ(q); }
    const mon = urlParam(search, 'mon');
    if (mon && /^\d{4}-\d{2}$/.test(mon)) setMon?.(mon);
    // 주소가 바뀔 때만 — setter 는 안정적이라고 가정하지 않는다(화면마다 다르다).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
}
